import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite, listWhistle, transitionWhistle, type WhistleReport, type WhistleStatus } from '../lib/api'
import { PageHead, Skel, Empty } from '../components/ui'
import { thaiDateTime } from '../lib/format'

const catLabel: Record<string, string> = {
  fraud: 'ทุจริต', bribery: 'สินบน', harassment: 'ล่วงละเมิด', safety: 'ความปลอดภัย', other: 'อื่น ๆ',
}
const statusLabel: Record<string, string> = {
  received: 'รอรับเรื่อง', reviewing: 'กำลังตรวจสอบ', closed: 'ปิดเรื่อง',
}
const filters: { value: WhistleStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'ทั้งหมด' },
  { value: 'received', label: 'รับเรื่อง' },
  { value: 'reviewing', label: 'กำลังตรวจสอบ' },
  { value: 'closed', label: 'ปิดเรื่อง' },
]

export function WhistleTriage() {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['whistle'], queryFn: listWhistle })
  const [filter, setFilter] = useState<WhistleStatus | 'all'>('all')
  const [open, setOpen] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [engagement, setEngagement] = useState('')
  const [caseMsg, setCaseMsg] = useState('')

  const eng = useQuery({
    queryKey: ['engagements'],
    queryFn: () => api<{ id: string; code: string; title: string }[]>('/v1/engagements'),
    staleTime: 60_000,
  })

  const openCase = useMutation({
    mutationFn: (id: string) => apiWrite<{ case_no: string }>(`/v1/whistle/${id}/case`, 'POST', {
      engagement_id: engagement,
      title: 'เรื่องแจ้งเบาะแส',
    }),
    onSuccess: (res) => {
      setCaseMsg(`เปิดเคส ${res.case_no} แล้ว — ติดตามได้ที่หน้าเคสตรวจสอบ`)
      void qc.invalidateQueries({ queryKey: ['whistle'] })
    },
    onError: (e) => setCaseMsg((e as Error).message),
  })

  const move = useMutation({
    mutationFn: ({ id, status }: { id: string; status: WhistleStatus }) => transitionWhistle(id, status, note),
    onSuccess: () => { setOpen(null); setNote(''); void qc.invalidateQueries({ queryKey: ['whistle'] }) },
  })

  const rows = useMemo(
    () => (q.data ?? []).filter((r) => filter === 'all' || r.status === filter),
    [q.data, filter],
  )

  return (
    <>
      <PageHead title="แจ้งเบาะแส" sub="ช่องทางลับ · ไม่เก็บไอพีหรือข้อมูลอุปกรณ์ของผู้แจ้ง" />

      <div className="row" style={{ gap: 8, marginBottom: 16 }}>
        {filters.map((f) => (
          <button key={f.value} className={`chip${filter === f.value ? ' chip-on' : ''}`} onClick={() => setFilter(f.value)}>
            {f.label}
          </button>
        ))}
      </div>

      {q.isLoading ? <Skel h={220} /> : rows.length === 0 ? (
        <Empty title="ยังไม่มีเรื่องที่แจ้งเข้ามา" body="เมื่อมีผู้แจ้งเบาะแสผ่านลิงก์สาธารณะ เรื่องจะปรากฏที่นี่" />
      ) : (
        <div className="card">
          {rows.map((r: WhistleReport) => (
            <div key={r.id} style={{ borderBottom: '1px solid var(--color-hairline)' }}>
              <button className="whistle-row" onClick={() => { setOpen(open === r.id ? null : r.id); setNote(r.auditor_note ?? '') }}>
                <span className="dim" style={{ width: 132, fontSize: 11.5 }}>{thaiDateTime(r.created_at)}</span>
                <span style={{ width: 96, fontSize: 12 }}>{catLabel[r.category] ?? r.category}</span>
                <span style={{ flex: 1, textAlign: 'left', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.detail}
                </span>
                <span className={`badge badge-${r.status}`} style={{ width: 108, justifyContent: 'center' }}>
                  {statusLabel[r.status] ?? r.status}
                </span>
              </button>

              {open === r.id && (
                <div className="whistle-detail">
                  <p style={{ fontSize: 13.5, lineHeight: 1.75, margin: 0 }}>{r.detail}</p>
                  {r.evidence_note && <p className="dim" style={{ fontSize: 12, marginTop: 8 }}>หลักฐาน: {r.evidence_note}</p>}
                  {r.contact && <p className="dim" style={{ fontSize: 12 }}>ติดต่อกลับ: {r.contact}</p>}

                  <div className="field">
                    <label htmlFor={`note-${r.id}`}>บันทึกของนักตรวจสอบ</label>
                    <textarea id={`note-${r.id}`} rows={3} className="input" value={note} onChange={(e) => setNote(e.target.value)}
                      placeholder="สรุปการดำเนินการ (ไม่เกิน 2000 ตัวอักษร)" />
                  </div>

                  <div className="row" style={{ gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
                    <select className="input" style={{ maxWidth: 320, height: 34 }} value={engagement}
                      onChange={(e) => setEngagement(e.target.value)}>
                      <option value="">เลือกโครงการเพื่อเปิดเคส…</option>
                      {(eng.data ?? []).map((e) => <option key={e.id} value={e.id}>{e.code} · {e.title}</option>)}
                    </select>
                    <button className="btn" disabled={!engagement || openCase.isPending}
                      onClick={() => openCase.mutate(r.id)}>
                      {openCase.isPending ? 'กำลังเปิดเคส…' : 'เปิดเคสจากเรื่องนี้'}
                    </button>
                  </div>
                  {caseMsg && <div className="dim" style={{ fontSize: 11.5, marginTop: 8 }}>{caseMsg}</div>}

                  <div className="row" style={{ gap: 8, marginTop: 12 }}>
                    {r.status !== 'reviewing' && r.status !== 'closed' && (
                      <button className="btn btn-primary" disabled={move.isPending}
                        onClick={() => move.mutate({ id: r.id, status: 'reviewing' })}>รับเข้าตรวจสอบ</button>
                    )}
                    {r.status !== 'closed' && (
                      <button className="btn" disabled={move.isPending}
                        onClick={() => move.mutate({ id: r.id, status: 'closed' })}>ปิดเรื่อง</button>
                    )}
                    <span className="dim" style={{ fontSize: 11.5, alignSelf: 'center' }}>
                      เปลี่ยนได้ตามลำดับ: รับเรื่อง → กำลังตรวจสอบ → ปิดเรื่อง
                    </span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="dim" style={{ fontSize: 11.5, marginTop: 14 }}>
        ลิงก์สำหรับผู้แจ้ง: <code>/whistle</code> — ส่งต่อได้โดยไม่ต้องล็อกอิน
      </div>
    </>
  )
}
